<div class="container">
  <sca-module-title class="title-test">
    <h1>Navigation Playground</h1>
    <span scaModuleTitlePost>Testing de @argo/navigation - {{ coverage }}% cobertura</span>
  </sca-module-title>

  <!-- Estado y Fase -->
  <section class="card">
    <h2>Estado de Navegación</h2>
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      NavigationStore utiliza NavigationPhase para gestionar el ciclo de vida de navegación
    </sca-alert>
    
    <div class="status-grid">
      <sca-card-workshop [selected]="navStore.phase$() === CommonPhase.IDLE">
        <header scaCardWorkshopHeader>
          <sca-badge variant="default" [rounded]="false">IDLE</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Sin inicializar</p>
        <p scaCardWorkshopAddress>Estado inicial del sistema</p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="navStore.phase$() === NavigationPhase.NAVIGATING">
        <header scaCardWorkshopHeader>
          <sca-badge variant="warning" [rounded]="false">NAVIGATING</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Navegando</p>
        <p scaCardWorkshopAddress>Transición en curso</p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="navStore.phase$() === NavigationPhase.NAVIGATED">
        <header scaCardWorkshopHeader>
          <sca-badge variant="success" [rounded]="false">NAVIGATED</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Navegado OK</p>
        <p scaCardWorkshopAddress>Ruta cargada correctamente</p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="navStore.phase$() === CommonPhase.ERROR">
        <header scaCardWorkshopHeader>
          <sca-badge variant="error" [rounded]="false">ERROR</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Error</p>
        <p scaCardWorkshopAddress>Navegación fallida</p>
      </sca-card-workshop>
    </div>
    
    @if (navStore.error$()) {
      <sca-alert variant="error" style="margin-top: 1rem;">
        <sca-icon icon="error" size="24px" [weight]="300" left />
        <strong>Error:</strong> {{ navStore.error$() }}
      </sca-alert>
    }
  </section>

  <!-- Computed Properties -->
  <section class="card">
    <h2>Computed Properties</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Señales reactivas computadas del NavigationStore
    </sca-alert>
    
    <table class="properties-table">
      <thead>
        <tr>
          <th>Propiedad</th>
          <th>Tipo</th>
          <th>Valor Actual</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>currentUrl$()</code></td>
          <td>Signal&lt;string&gt;</td>
          <td><span class="value-enum">{{ navStore.currentUrl$() || 'N/A' }}</span></td>
          <td>URL actual de la navegación</td>
        </tr>
        <tr>
          <td><code>currentRoute$()</code></td>
          <td>Signal&lt;string&gt;</td>
          <td><span class="value-enum">{{ navStore.currentRoute$() || 'N/A' }}</span></td>
          <td>Ruta actual de la navegación</td>
        </tr>
        <tr>
          <td><code>history$()</code></td>
          <td>Signal&lt;NavigationEvent[]&gt;</td>
          <td><span class="value-number">{{ navStore.history$().length }} eventos</span></td>
          <td>Historial de eventos de navegación</td>
        </tr>
        <tr>
          <td><code>actions$()</code></td>
          <td>Signal&lt;NavigationAction[]&gt;</td>
          <td><span class="value-number">{{ navStore.actions$().length }} acciones</span></td>
          <td>Acciones de navegación registradas</td>
        </tr>
        <tr>
          <td><code>isNavigating$()</code></td>
          <td>Computed&lt;boolean&gt;</td>
          <td><span [class]="navStore.isNavigating$() ? 'value-true' : 'value-false'">{{ navStore.isNavigating$() }}</span></td>
          <td>Indica si hay navegación en curso</td>
        </tr>
        <tr>
          <td><code>isNavigated$()</code></td>
          <td>Computed&lt;boolean&gt;</td>
          <td><span [class]="navStore.isNavigated$() ? 'value-true' : 'value-false'">{{ navStore.isNavigated$() }}</span></td>
          <td>Indica si la navegación completó</td>
        </tr>
        <tr>
          <td><code>isError$()</code></td>
          <td>Computed&lt;boolean&gt;</td>
          <td><span [class]="navStore.isError$() ? 'value-warning' : 'value-false'">{{ navStore.isError$() }}</span></td>
          <td>Indica si hubo error en navegación</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- URL y Ruta Actual -->
  <section class="card">
    <h2>URL y Ruta Actual</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Información sobre la ubicación actual en la aplicación
    </sca-alert>

    <div class="utilities-grid">
      <sca-card-workshop [selected]="true">
        <header scaCardWorkshopHeader>
          <sca-icon icon="public" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>URL Completa</p>
        <p scaCardWorkshopAddress style="word-break: break-all; font-family: monospace; font-size: 0.85rem;">
          {{ navStore.currentUrl$() || 'No inicializado' }}
        </p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="true">
        <header scaCardWorkshopHeader>
          <sca-icon icon="alt_route" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Ruta Angular</p>
        <p scaCardWorkshopAddress style="word-break: break-all; font-family: monospace; font-size: 0.85rem;">
          {{ navStore.currentRoute$() || 'No inicializado' }}
        </p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Métodos Públicos -->
  <section class="card">
    <h2>Métodos de Navegación</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Funciones disponibles para controlar la navegación programática
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="play_arrow" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>initNavigation()</p>
        <p scaCardWorkshopAddress>
          Inicializa el sistema de navegación, suscribe a eventos Router, captura NavigationStart y NavigationEnd
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="navigation" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>navigateTo(url, extras?)</p>
        <p scaCardWorkshopAddress>
          Navega a una ruta específica, cambia fase a NAVIGATING, ejecuta router.navigate()
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="arrow_back" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>goBack()</p>
        <p scaCardWorkshopAddress>
          Navega hacia atrás en el historial usando location.back()
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="arrow_forward" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>goForward()</p>
        <p scaCardWorkshopAddress>
          Navega hacia adelante en el historial usando location.forward()
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="list_alt" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>getNavigationHistory()</p>
        <p scaCardWorkshopAddress>
          Retorna array de NavigationEventModel con eventos Angular y window.open
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="fact_check" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>getUserActionsLog()</p>
        <p scaCardWorkshopAddress>
          Retorna array de NavigationUserActionModel con clicks, teclas, touch, drag (máximo 10)
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="settings" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>setUserActionsToLog(actions)</p>
        <p scaCardWorkshopAddress>
          Configura qué tipos de eventos capturar: click, keyup, keydown, touchmove, mousemove, drag
        </p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Historial de Navegación -->
  <section class="card">
    <h2>Historial de Navegación</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Registro completo de eventos: <strong>{{ navStore.history$().length }}</strong> eventos capturados
    </sca-alert>

    @if (navStore.history$().length > 0) {
      <div class="utilities-grid">
        @for (event of navStore.history$(); track $index) {
          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              @if (event.type === 'angular.navigation') {
                <sca-badge variant="success" [rounded]="false">ANGULAR</sca-badge>
              }
              @if (event.type === 'window.tab') {
                <sca-badge variant="accent" [rounded]="false">TAB</sca-badge>
              }
              @if (event.type === 'window.popup') {
                <sca-badge variant="warning" [rounded]="false">POPUP</sca-badge>
              }
            </header>
            <p scaCardWorkshopTitle>#{{ $index + 1 }} - {{ event.type }}</p>
            <p scaCardWorkshopAddress style="word-break: break-all; font-size: 0.85rem;">
              <strong>URL:</strong> {{ event.url }}<br>
              @if (event.prev) {
                <strong>Desde:</strong> {{ event.prev }}
              }
            </p>
          </sca-card-workshop>
        }
      </div>
    } @else {
      <sca-alert variant="warning">
        <sca-icon icon="inbox" size="24px" [weight]="300" left />
        No hay eventos de navegación registrados. Los eventos se capturan automáticamente cuando navegas.
      </sca-alert>
    }
  </section>

  <!-- Acciones de Usuario -->
  <section class="card">
    <h2>Log de Acciones de Usuario</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Captura de interacciones DOM: <strong>{{ navStore.actions$().length }}</strong> acciones registradas
    </sca-alert>

    @if (navStore.actions$().length > 0) {
      <div class="utilities-grid">
        @for (action of navStore.actions$(); track $index) {
          <sca-card-workshop>
            <header scaCardWorkshopHeader>
              @if (action.type === 'click') {
                <sca-badge variant="error" [rounded]="false">CLICK</sca-badge>
              }
              @if (action.type === 'keyup' || action.type === 'keydown') {
                <sca-badge variant="accent" [rounded]="false">KEY</sca-badge>
              }
              @if (action.type === 'touchmove') {
                <sca-badge variant="success" [rounded]="false">TOUCH</sca-badge>
              }
              @if (action.type === 'mousemove') {
                <sca-badge variant="default" [rounded]="false">MOUSE</sca-badge>
              }
              @if (action.type.includes('drag')) {
                <sca-badge variant="warning" [rounded]="false">DRAG</sca-badge>
              }
            </header>
            <p scaCardWorkshopTitle>#{{ $index + 1 }} - {{ action.type }}</p>
            <p scaCardWorkshopAddress>
              <strong>Elemento:</strong> <code>{{ action.domElement }}</code>
            </p>
          </sca-card-workshop>
        }
      </div>
    } @else {
      <sca-alert variant="warning">
        <sca-icon icon="inbox" size="24px" [weight]="300" left />
        No hay acciones capturadas. Configura los eventos con setUserActionsToLog().
      </sca-alert>
    }
  </section>

  <!-- Interceptores y Analíticas -->
  <section class="card">
    <h2>Interceptores y Analíticas</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Captura automática de eventos para Google Tag Manager (GTM)
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="open_in_new" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Window.open Interceptor</p>
        <p scaCardWorkshopAddress>
          Intercepta window.open(), detecta popups vs pestañas, registra eventos en historial
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="analytics" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Page View Events</p>
        <p scaCardWorkshopAddress>
          Envía evento GTM en cada NavigationStart: event: 'page', pageName: url
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="ads_click" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Click Analytics</p>
        <p scaCardWorkshopAddress>
          Captura clicks en elementos medibles configurados en ArgoConfig
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="touch_app" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>DOM Event Handlers</p>
        <p scaCardWorkshopAddress>
          Eventos: click, keyup, keydown, touchmove, mousemove, drag, dragstart, drop
        </p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Modelos e Interfaces -->
  <section class="card">
    <h2>Modelos e Interfaces</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Estructuras de datos utilizadas por NavigationStore
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="code" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>NavigationEventModel</p>
        <p scaCardWorkshopAddress>
          prev: string, type: string, url: string - Almacena eventos del historial
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="code" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>NavigationUserActionModel</p>
        <p scaCardWorkshopAddress>
          type: string, domElement: string - Almacena acciones de usuario
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="code" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>NavigationState</p>
        <p scaCardWorkshopAddress>
          Estado interno: currentUrl, currentRoute, history, actions, phase, error
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="category" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>NavigationPhase enum</p>
        <p scaCardWorkshopAddress>
          NAVIGATING, NAVIGATED + CommonPhase.IDLE, CommonPhase.ERROR
        </p>
      </sca-card-workshop>
    </div>
  </section>
</div>
